import React, { useEffect, useState, useCallback } from 'react';
import { Copy, Check, Gift, Share2 } from 'lucide-react';
import { get } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './shadcn/card';
import { Button } from './shadcn/button';
import { ReferralProgressBar, PRIZE_EMOJI, formatPoints, referralShareText } from './ReferralWidgets';

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// One journey per friend: Signed up → Free trial → Subscribed. A friend is shown at their
// furthest step (Subscribed includes having done the trial), so there is never a choice
// between two "confirmed" labels.
const STEPS = ['Signed up', 'Free trial', 'Subscribed'];
const STAGE_INDEX = { pending: 0, confirmed_trial: 1, confirmed_paid: 2 };
const STAGE_SUMMARY = {
  pending: { text: 'Signed up', className: 'text-gray-600' },
  confirmed_trial: { text: 'Free trial done · 0.5 pt', className: 'text-green-700' },
  confirmed_paid: { text: 'Subscribed · 1 pt', className: 'text-green-700' },
};

function JourneyStepper({ status }) {
  const reached = STAGE_INDEX[status] ?? 0;
  const summary = STAGE_SUMMARY[status] || STAGE_SUMMARY.pending;
  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center">
        {STEPS.map((label, i) => {
          const done = i <= reached;
          return (
            <React.Fragment key={label}>
              {i > 0 && <div className={`h-0.5 w-6 sm:w-8 ${i <= reached ? 'bg-green-500' : 'bg-gray-200'}`} />}
              <div
                className={`flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                  done ? 'border-green-500 bg-green-500' : 'border-gray-300 bg-white'
                }`}
                title={label}
                aria-label={`${label}${done ? ' (reached)' : ''}`}
              >
                {done && <Check className="h-2.5 w-2.5 text-white" strokeWidth={4} />}
              </div>
            </React.Fragment>
          );
        })}
      </div>
      <span className={`text-xs font-semibold ${summary.className}`}>{summary.text}</span>
    </div>
  );
}

export default function ReferAndEarn() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await get('/referrals/mine', true);
      setData(res);
    } catch (e) {
      setError('Could not load your referral details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const shareText = data ? referralShareText(data.link) : '';

  const copyLink = async () => {
    if (!data?.link) return;
    try {
      await navigator.clipboard.writeText(data.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Fallback: select-able input below
    }
  };

  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank', 'noopener');
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Nova Learning', text: shareText });
      } catch (e) {
        /* user cancelled */
      }
    } else {
      copyLink();
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-56 rounded bg-gray-200" />
          <div className="h-40 rounded-xl bg-gray-200" />
          <div className="h-32 rounded-xl bg-gray-200" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-4xl p-6 text-center">
        <p className="mb-4 text-red-600">{error || 'Something went wrong.'}</p>
        <Button onClick={load}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6 font-poppins">
      <div>
        <h1 className="font-fredoka text-3xl font-bold text-[#2F1E6B]">Refer &amp; Earn</h1>
        <p className="mt-1 text-gray-600">
          Give a friend 25% off their first month — and earn rewards every time they join.
        </p>
      </div>

      {/* Progress */}
      <Card className="border-[#E6DDFB] bg-gradient-to-br from-white to-[#F7F4FE]">
        <CardContent className="pt-6">
          <p className="text-lg font-semibold text-[#2F1E6B]">{data.headline}</p>
          <ReferralProgressBar points={data.points} prizes={data.prizes} />
          <p className="mt-2 text-sm text-gray-700">{data.subline}</p>
        </CardContent>
      </Card>

      {/* Share */}
      <Card>
        <CardHeader>
          <CardTitle className="text-[#5C3BD1]">Your invite link</CardTitle>
          <CardDescription>Share it anywhere — your friend gets 25% off their first month.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              readOnly
              value={data.link}
              onFocus={(e) => e.target.select()}
              className="flex-1 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-800"
            />
            <Button onClick={copyLink} variant="outline" className="gap-2">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
            <Button onClick={shareWhatsApp} className="gap-2 bg-[#25D366] text-white hover:bg-[#1ebe5b]">
              <Share2 className="h-4 w-4" /> WhatsApp
            </Button>
            <Button onClick={nativeShare} variant="outline" className="gap-2 sm:hidden">
              <Share2 className="h-4 w-4" /> Share
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Points + counts */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs uppercase tracking-wide text-gray-500">Points</p>
            <p className="text-3xl font-bold text-[#5C3BD1]">{formatPoints(data.points)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs uppercase tracking-wide text-gray-500">Signed up</p>
            <p className="text-3xl font-bold text-gray-700">{data.pendingCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs uppercase tracking-wide text-gray-500">Free trial done</p>
            <p className="text-3xl font-bold text-green-700">{data.confirmedTrialCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-xs uppercase tracking-wide text-gray-500">Subscribed</p>
            <p className="text-3xl font-bold text-green-700">{data.confirmedPaidCount}</p>
          </CardContent>
        </Card>
      </div>
      <p className="-mt-3 text-xs text-gray-500">Each friend is counted at their furthest step.</p>

      {/* Prize ladder */}
      <Card>
        <CardHeader>
          <CardTitle className="text-[#5C3BD1]">Rewards</CardTitle>
          <CardDescription>
            <strong>1 point</strong> for every friend who subscribes. If they complete the free trial but don't
            subscribe, you still get <strong>0.5 points</strong>.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {data.prizes.map((p) => (
              <div
                key={p.key}
                className={`rounded-xl border p-4 text-center ${
                  p.earned ? 'border-[#5C3BD1] bg-[#F3EFFD]' : 'border-gray-200 bg-white'
                }`}
              >
                <div className="text-3xl">{PRIZE_EMOJI[p.key] || '⭐'}</div>
                <p className="mt-2 text-sm font-semibold text-[#2F1E6B]">{p.label}</p>
                <p className="text-xs text-gray-500">
                  {p.points} point{p.points === 1 ? '' : 's'}
                </p>
                {p.earned && <p className="mt-1 text-xs font-semibold text-[#5C3BD1]">✓ Unlocked</p>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Referral list */}
      <Card>
        <CardHeader>
          <CardTitle className="text-[#5C3BD1]">Your referrals</CardTitle>
          <CardDescription>
            Every friend follows the same journey: signed up → free trial → subscribed. You earn 0.5 points when
            their free trial is done, and the full 1 point once they subscribe.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data.referrals.length === 0 ? (
            <p className="text-sm text-gray-500">No referrals yet — share your link to get started.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {data.referrals.map((r, i) => (
                <li key={`${r.firstName}-${r.createdAt}-${i}`} className="flex items-center justify-between gap-3 py-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#E6DDFB] font-semibold text-[#2F1E6B]">
                      {(r.firstName || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate font-medium text-[#2F1E6B]">{r.firstName}</p>
                      <p className="text-xs text-gray-500">{formatDate(r.createdAt)}</p>
                    </div>
                  </div>
                  <JourneyStepper status={r.displayStatus} />
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* How it works */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#5C3BD1]">
            <Gift className="h-5 w-5" /> How it works
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal space-y-2 pl-5 text-sm text-gray-700">
            <li>Share your invite link with a friend.</li>
            <li>They sign up, get 25% off their first month, and start a free trial.</li>
            <li>
              You earn <strong>1 point</strong> when they subscribe — and <strong>0.5 points</strong> as soon as their free
              trial is complete, even if they don't. Prizes unlock at 1, 3, 5 and 10 points.
            </li>
          </ol>
          <p className="mt-3 text-xs text-gray-500">
            Points are awarded once per friend. Invites can't be used on your own account.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
